<template>
  <div v-if="loading" class="page-loading">
    <Loading />
  </div>
  <div>
    <main class="profile-page page">
      <div class="container">
        <div class="profile-card">
          <div class="user-info">
            <div class="avatar" v-if="email">
                <span class="material-symbols-outlined">
                  mail
                </span>
            </div>
            <div class="user-details">
              <h1>{{ email }}</h1>
            </div>
          </div>
        </div>

        <div class="profile-card">
          <span class="title-span">Reset Password</span>

          <form @submit.prevent="handleResetPassword" class="password-form">
            <div class="form-group">
              <label for="new-password">New Password</label>
              <div class="password-input">
                <input
                  id="new-password"
                  :type="showNewPassword ? 'text' : 'password'"
                  v-model="newPassword"
                  required
                  @focus="showRequirements = true"
                  @blur="showRequirements = false"
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('new')"
                  class="toggle-password"
                >
                  {{ showNewPassword ? "👁️" : "🙈" }}
                </button>
              </div>
              <password-strength-indicator
                :password="newPassword"
                :show-requirements="showRequirements"
              />
            </div>

            <div class="form-group">
              <label for="confirm-password">Confirm New Password</label>
              <div class="password-input">
                <input
                  id="confirm-password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  v-model="confirmPassword"
                  required
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('confirm')"
                  class="toggle-password"
                >
                  {{ showConfirmPassword ? "👁️" : "🙈" }}
                </button>
              </div>
            </div>

            <button
              type="submit"
              class="submit-button"
              :disabled="!passwordsMatch"
            >
              <span>Update Password</span>
            </button>
          </form>
        </div>
      </div>
    </main>
  </div>

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    :duration="2000"
    @closed="!notification.show"
  />
</template>

<script>
import Loading from "@/components/user/layout/LoadingComponent.vue";
import PasswordStrengthIndicator from "@/components/user/viewComponent/profile/PasswordStrengthIndicator.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import { handleResetPassword } from "@/api/auth.api";

export default {
  name: "ResetPassword",
  components: {
    Loading,
    PasswordStrengthIndicator,
    NotificationComponent,
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      user: [],
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      token: '',
      email: '',
      isLoading: false,
      errorMessage: "",
      successMessage: "",
      showPassword: false,
      showRequirements: false,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      loading: false,
    };
  },
  async created() {
    this.loading = true;
    this.token = this.$route.query.token
    this.email = decodeURIComponent(this.$route.query.email)
    if (!this.token || !this.email) {
      this.redirectToLogin();
    }
    this.loading = false;
  },
  computed: {
    passwordsMatch() {
      return this.newPassword === this.confirmPassword;
    },
  },
  methods: {
    async handleResetPassword() {
      if (!this.passwordsMatch) {
          this.showNotification(
            "hold",
            this.$t("Custom.hold"),
            "Passwords do not match",
          );        
        this.errorMessage = "Passwords do not match";
        return;
      }
      this.loading = true;
      try {
        const response = await handleResetPassword(
          this.token,
          this.email,
          this.newPassword,
          this.confirmPassword
        );
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            this.$t(response.data.msg)
          );
          setTimeout(() => {
            this.$router.push("/login");
          }, 1000);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            this.$t(response.data.msg)
          );
          if (response.data.errNum == 400) {
            setTimeout(() => {
              this.$router.push("/login");
            }, 1000);
          }
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    redirectToLogin() {
      this.$router.push("/login");
    },
    togglePasswordVisibility(field) {
      if (field === "current") {
        this.showCurrentPassword = !this.showCurrentPassword;
      } else if (field === "new") {
        this.showNewPassword = !this.showNewPassword;
      } else if (field === "confirm") {
        this.showConfirmPassword = !this.showConfirmPassword;
      }
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
  },
};
</script>

<style scoped>
/* Layout */
.profile-page {
  padding: 25px;
}

.container {
  max-width: 768px;
  margin: 0 auto;
}

/* Cards */
.profile-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 24px;
}
.title-span {
  font-size: 18px;
  font-weight: bold;
}
/* User Information */
.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar {
  width: 64px;
  height: 64px;
  background-color: var(--hover-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar span {
  font-size: 24px;
  color: var(--logo-color);
  font-weight: bold;
}

.user-details h1 {
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
}

.email {
  display: flex;
  align-items: center;
  color: #4b5563;
  margin-bottom: 4px;
}

.email-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
}

.join-date {
  font-size: 14px;
  color: #6b7280;
}

/* Form Styles */
.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.password-input {
  position: relative;
}

.password-input input {
  width: 100%;
  padding: 8px 16px;
  padding-right: 40px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.password-input input:focus {
  outline: none;
  border-color: var(--logo-color);
  box-shadow: 0 0 0 3px rgba(199, 120, 2, 0.1);
}

.toggle-password {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #6b7280;
}

/* Messages */
.message-container {
  min-height: 24px;
}

.error-message {
  color: #dc2626;
  font-size: 14px;
}

.success-message {
  color: #16a34a;
  font-size: 14px;
}

/* Submit Button */
.submit-button {
  background-color: var(--logo-color);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-button:hover:not(:disabled) {
  background-color: var(--logo-hover-color);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading Spinner */
.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .profile-page {
    padding: 10px;
  }

  .user-info {
    flex-direction: column;
    text-align: center;
  }

  .user-details {
    align-items: center;
  }

  .email {
    justify-content: center;
  }
}
</style>
